/**
 * `/benchmark` — the public "Agent Pass Rate" report (moat plan §7 flywheel).
 * Landing-styled (`.mk`, light-only, no shadcn here); numbers come from `GET /api/public/benchmark`,
 * which already hides every group below 5 tasks and excludes users who opted out.
 * Charts are inline SVG on purpose: one measure, one hue, no chart library.
 */
import { createFileRoute } from '@tanstack/react-router'
import { Container, MkFooter, MkLink, MkNav, Overline, SectionHead } from '#/components/landing/mk'
import { overallFirstPass, useBenchmark, type BenchRow } from '#/lib/api-flywheel'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/benchmark')({
  head: () => ({
    meta: [
      { title: m.fw_bm_meta_title() },
      { name: 'description', content: m.fw_bm_meta_desc() },
      { property: 'og:title', content: m.fw_bm_meta_title() },
      { property: 'og:description', content: m.fw_bm_meta_desc() },
    ],
  }),
  component: Page,
})

const pct = (v: number) => `${Math.round(v * 100)}%`
const agentName = (a: string) => (a === 'unknown' ? m.fw_bm_unknown() : a)

/** One measure, one hue: a track plus the value, 4px rounded ends, no viewBox so strokes stay crisp. */
function Bar({ value, height = 10 }: { value: number; height?: number }) {
  return (
    <svg width="100%" height={height} role="presentation" className="block">
      <rect x="0" y="0" width="100%" height={height} rx={height / 2} fill="var(--mk-bg-muted)" />
      <rect x="0" y="0" width={`${Math.max(1.5, value * 100)}%`} height={height} rx={height / 2} fill="var(--mk-accent)" />
    </svg>
  )
}

function StatTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="mk-card-white flex flex-col gap-1 p-6">
      <span className="text-[13px] uppercase tracking-wide text-[var(--mk-fg-muted)]">{label}</span>
      <span className="text-4xl font-semibold tabular-nums text-[var(--mk-fg)]">{value}</span>
      {sub ? <span className="text-[13px] text-[var(--mk-fg-muted)]">{sub}</span> : null}
    </div>
  )
}

function AgentChart({ rows }: { rows: BenchRow[] }) {
  if (!rows.length) return <p className="mk-muted text-[15px]">{m.fw_bm_group_empty()}</p>
  return (
    <ul className="flex flex-col gap-4">
      {rows.map((r) => (
        <li key={r.agent} className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-4 gap-y-1.5 sm:grid-cols-[10rem_minmax(0,1fr)_5.5rem]">
          <span className="truncate text-[15px] text-[var(--mk-fg)]">{agentName(r.agent)}</span>
          <span className="order-last col-span-2 sm:order-none sm:col-span-1 sm:self-center">
            <Bar value={r.firstPassRate} />
          </span>
          <span className="justify-self-end text-right text-[15px] tabular-nums text-[var(--mk-fg)]">
            {pct(r.firstPassRate)}
            <span className="ml-2 text-[13px] text-[var(--mk-fg-muted)]">n={r.tasks}</span>
          </span>
        </li>
      ))}
    </ul>
  )
}

function Table({ head, rows, empty }: { head: string[]; rows: (string | number | null)[][]; empty: string }) {
  if (!rows.length) return <p className="mk-muted text-[15px]">{empty}</p>
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[36rem] border-collapse text-[15px]">
        <thead>
          <tr className="border-b border-[var(--mk-border)] text-left text-[13px] uppercase tracking-wide text-[var(--mk-fg-muted)]">
            {head.map((h, i) => (
              <th key={h} scope="col" className={i === 0 ? 'py-2.5 pr-4 font-normal' : 'py-2.5 pr-4 text-right font-normal'}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[var(--mk-border)] last:border-0">
              {r.map((c, j) => (
                <td key={j} className={j === 0 ? 'py-2.5 pr-4 text-[var(--mk-fg)]' : 'py-2.5 pr-4 text-right tabular-nums text-[var(--mk-fg)]'}>
                  {c ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Monthly first-pass rate. Uniform scaling + non-scaling strokes keep the 2px line honest at any width. */
function Trend({ points }: { points: { month: string; tasks: number; firstPassRate: number }[] }) {
  const W = 640
  const H = 150
  const pad = { l: 8, r: 8, t: 10, b: 10 }
  const n = points.length
  const x = (i: number) => (n < 2 ? W / 2 : pad.l + (i * (W - pad.l - pad.r)) / (n - 1))
  const y = (v: number) => pad.t + (1 - v) * (H - pad.t - pad.b)
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.firstPassRate).toFixed(1)}`).join(' ')
  const area = n > 1 ? `${line} L${x(n - 1).toFixed(1)},${H - pad.b} L${x(0).toFixed(1)},${H - pad.b} Z` : ''
  return (
    <figure className="flex flex-col gap-3">
      {/* width:100% + height:auto lets the viewBox ratio scale it uniformly — strokes stay 2px via vectorEffect */}
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={m.fw_bm_trend_title()} className="block h-auto w-full">
        {[0, 0.5, 1].map((g) => (
          <line key={g} x1="0" x2={W} y1={y(g)} y2={y(g)} stroke="var(--mk-border)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        ))}
        {area ? <path d={area} fill="var(--mk-accent)" opacity="0.08" /> : null}
        {n > 1 ? <path d={line} fill="none" stroke="var(--mk-accent)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" /> : null}
        {points.map((p, i) => (
          <circle key={p.month} cx={x(i)} cy={y(p.firstPassRate)} r="5" fill="var(--mk-accent)" stroke="var(--mk-bg)" strokeWidth="2" vectorEffect="non-scaling-stroke">
            <title>{`${p.month} · ${pct(p.firstPassRate)} · n=${p.tasks}`}</title>
          </circle>
        ))}
      </svg>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-[13px] text-[var(--mk-fg-muted)]">
        {points.map((p) => (
          <span key={p.month} className="tabular-nums">
            {p.month} · <span className="text-[var(--mk-fg)]">{pct(p.firstPassRate)}</span> · n={p.tasks}
          </span>
        ))}
      </div>
    </figure>
  )
}

function Page() {
  const { data, isLoading } = useBenchmark()
  const overall = overallFirstPass(data)
  const ready = Boolean(data && data.sampleTasks >= 5)
  return (
    <div className="mk h-full overflow-y-auto overflow-x-hidden scroll-smooth">
      <MkNav />
      <main>
        <section className="pb-10 pt-16 lg:pt-24">
          <Container className="flex flex-col items-center gap-6 text-center">
            <Overline>{m.fw_bm_overline()}</Overline>
            <h1 className="max-w-[16ch]">{m.fw_bm_title()}</h1>
            <p className="mk-lead max-w-[60ch]">{m.fw_bm_sub()}</p>
          </Container>
        </section>

        {isLoading ? (
          <section className="pb-16">
            <Container>
              <p className="mk-muted text-center text-[15px]">{m.fw_bm_loading()}</p>
            </Container>
          </section>
        ) : !ready ? (
          <section className="pb-16">
            <Container>
              <div className="mk-card mx-auto flex max-w-2xl flex-col gap-3 p-8 text-center">
                <h3>{m.fw_bm_empty_title()}</h3>
                <p className="mk-muted leading-relaxed">{m.fw_bm_empty_desc()}</p>
              </div>
            </Container>
          </section>
        ) : (
          <>
            <section className="pb-14">
              <Container className="grid gap-4 sm:grid-cols-3">
                <StatTile label={m.fw_bm_stat_sample()} value={data!.sampleTasks.toLocaleString()} />
                <StatTile label={m.fw_bm_stat_firstpass()} value={overall === null ? '—' : pct(overall)} />
                <StatTile label={m.fw_bm_stat_generated()} value={new Date(data!.generatedAt).toISOString().slice(0, 10)} sub={new Date(data!.generatedAt).toISOString().slice(11, 16) + ' UTC'} />
              </Container>
            </section>

            <section className="pb-14">
              <Container className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <h2>{m.fw_bm_agents_title()}</h2>
                  <p className="mk-muted max-w-[60ch]">{m.fw_bm_agents_lead()}</p>
                </div>
                <div className="mk-card-white p-7">
                  <AgentChart rows={data!.byAgent} />
                </div>
              </Container>
            </section>

            <section className="pb-14">
              <Container className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <h2>{m.fw_bm_kind_title()}</h2>
                  <p className="mk-muted max-w-[60ch]">{m.fw_bm_kind_lead()}</p>
                </div>
                <div className="mk-card-white p-7">
                  <Table
                    head={[m.fw_bm_col_kind(), m.fw_bm_col_layer(), m.fw_bm_col_tasks(), m.fw_bm_col_firstpass(), m.fw_bm_col_retries(), m.fw_bm_col_median()]}
                    rows={data!.byKind.map((r) => [r.kind, r.layer, r.tasks, pct(r.firstPassRate), r.retries.toFixed(2), r.medianMinutes === null ? null : m.fw_bm_minutes({ n: r.medianMinutes })])}
                    empty={m.fw_bm_group_empty()}
                  />
                </div>
              </Container>
            </section>

            <section className="pb-14">
              <Container className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <h2>{m.fw_bm_tier_title()}</h2>
                  <p className="mk-muted max-w-[60ch]">{m.fw_bm_tier_lead()}</p>
                </div>
                <div className="mk-card-white p-7">
                  <Table
                    head={[m.fw_bm_col_tier(), m.fw_bm_col_agent(), m.fw_bm_col_tasks(), m.fw_bm_col_firstpass(), m.fw_bm_col_retries(), m.fw_bm_col_median()]}
                    rows={data!.byTier.map((r) => [r.tier, agentName(r.agent), r.tasks, pct(r.firstPassRate), r.retries.toFixed(2), r.medianMinutes === null ? null : m.fw_bm_minutes({ n: r.medianMinutes })])}
                    empty={m.fw_bm_group_empty()}
                  />
                </div>
              </Container>
            </section>

            <section className="pb-14">
              <Container className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <h2>{m.fw_bm_trend_title()}</h2>
                  <p className="mk-muted max-w-[60ch]">{m.fw_bm_trend_lead()}</p>
                </div>
                <div className="mk-card-white p-7">
                  <Trend points={data!.monthly} />
                </div>
              </Container>
            </section>
          </>
        )}

        <section className="pb-14">
          <Container>
            <div className="mk-card flex flex-col gap-3 p-7">
              <h3>{m.fw_bm_method_title()}</h3>
              <ul className="flex list-disc flex-col gap-2 pl-5">
                <li className="mk-muted leading-relaxed">{m.fw_bm_method_1()}</li>
                <li className="mk-muted leading-relaxed">{m.fw_bm_method_2()}</li>
                <li className="mk-muted leading-relaxed">{m.fw_bm_method_3()}</li>
                <li className="mk-muted leading-relaxed">{m.fw_bm_method_4()}</li>
              </ul>
            </div>
          </Container>
        </section>

        <section className="py-16 lg:py-20">
          <Container>
            <SectionHead
              title={m.fw_bm_cta_title()}
              lead={m.fw_bm_cta_desc()}
              actions={
                <>
                  <MkLink to="/plan" search={{ flow: 'new' }}>
                    {m.fw_bm_cta_btn()}
                  </MkLink>
                  <MkLink to="/templates" variant="secondary" arrow>
                    {m.fw_tpl_back()}
                  </MkLink>
                </>
              }
            />
          </Container>
        </section>
      </main>
      <MkFooter />
    </div>
  )
}
